<script setup>
import { ref, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import { useTerritoriosStore } from "@/store/territorios";
import { useCatalogos } from "@/composables/useCatalogos";
import { useAppToast } from "@/composables/useAppToast";

defineProps({
  visible: Boolean,
});
const emit = defineEmits(["update:visible"]);

const territorios = useTerritoriosStore();
const cat = useCatalogos();
const toast = useAppToast();

const saving = ref(false);
const saved = ref(false);

const addGrupo = async () => {
  const created = await cat.createGrupo({ name: "" });
  if (created?.error) return toast.error("Ocurrió un error", created.error);
  territorios.grupos.push({ id: created.id, name: created.name ?? "", zonaId: null });
};

const removeGrupo = async (id) => {
  const res = await cat.deleteGrupo(id);
  if (res?.error) return toast.error("Ocurrió un error", res.error);
  territorios.grupos = territorios.grupos.filter((g) => g.id !== id);
};

const save = async () => {
  saving.value = true;
  try {
    await Promise.all(
      territorios.grupos.map((g) => cat.updateGrupo(g.id, { name: g.name, zonaId: g.zonaId }))
    );
  } catch (e) {
    console.error(e);
    toast.error("Ocurrió un error", "No se pudieron guardar los cambios.");
    saving.value = false;
    return;
  }
  saving.value = false;
  saved.value = true;
  setTimeout(() => (saved.value = false), 2500);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    header="Grupos de Predicación"
    :style="{ width: '460px' }"
  >
    <div class="text-sm text-500 mb-3">Editá el nombre directamente en cada campo.</div>
    <div class="item-list">
      <div v-for="item in territorios.grupos" :key="item.id" class="item-row">
        <InputText v-model="item.name" class="flex-1" placeholder="Nombre del grupo" />
        <Dropdown
          v-model="item.zonaId"
          :options="territorios.zonas"
          option-label="name"
          option-value="id"
          placeholder="Zona"
          showClear
          class="flex-1"
        />
        <Button
          icon="pi pi-trash"
          text
          rounded
          severity="danger"
          size="small"
          v-tooltip.top="'Eliminar'"
          @click="removeGrupo(item.id)"
        />
      </div>
      <div v-if="!territorios.grupos.length" class="empty-state">
        Sin grupos configurados
      </div>
    </div>
    <Button
      icon="pi pi-plus"
      label="Agregar grupo"
      outlined
      size="small"
      class="mt-2"
      @click="addGrupo"
    />
    <template #footer>
      <Button label="Cerrar" text @click="emit('update:visible', false)" />
      <Button
        :label="saved ? 'Guardado' : 'Guardar'"
        :icon="saved ? 'pi pi-check' : 'pi pi-save'"
        :loading="saving"
        @click="save"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.empty-state {
  font-size: var(--text-callout);
  color: var(--label-tertiary);
  padding: 0.5rem 0;
  font-style: italic;
}
</style>
